/**
 * MobilePreviewCard - 移动端预览卡片
 * 可折叠的顶部小卡片，显示当前配置摘要
 */
import { motion, AnimatePresence } from 'framer-motion';
import { useCreationStore } from '../../../store/useCreationStore';
import { ChevronDown, User, Sparkles } from 'lucide-react';

const themeLabels: Record<string, string> = {
  friendship: '友谊与冒险',
  bravery: '勇气与成长',
  family: '家庭与爱',
  nature: '自然与探索',
  dream: '梦想与未来',
  magic: '魔法与奇幻',
};

const styleLabels: Record<string, string> = {
  beatrix_potter: '温馨水彩',
  pixar: '鲜艳立体',
  disney: '梦幻童话',
  sketch: '铅笔素描',
};

function getStyleLabel(key: string) {
  return styleLabels[key] || key;
}

interface MobilePreviewCardProps {
  isExpanded: boolean;
  onToggle: () => void;
}

export default function MobilePreviewCard({ isExpanded, onToggle }: MobilePreviewCardProps) {
  const { step, character, story, visual } = useCreationStore();

  return (
    <div className="bg-white rounded-2xl shadow-md border border-slate-100 overflow-hidden">
      {/* 折叠头部 - 始终显示 */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between bg-gradient-to-r from-indigo-50 to-violet-50"
      >
        <div className="flex items-center gap-3">
          {/* 角色小头像 */}
          {(character.photoUrl || character.photoPreview) ? (
            <img
              src={character.photoUrl || character.photoPreview}
              alt={character.name}
              className="w-10 h-10 rounded-full object-cover border-2 border-white shadow"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
              <User className="w-5 h-5 text-slate-400" />
            </div>
          )}
          
          <div className="text-left">
            <div className="font-semibold text-slate-800 text-sm">
              {character.name || '未命名角色'}的绘本
            </div>
            <div className="text-xs text-slate-500">
              {story.pages || 12}页 · {getStyleLabel(visual.style)}
            </div>
          </div>
        </div>

        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="w-5 h-5 text-slate-400" />
        </motion.div>
      </button>

      {/* 展开内容 */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="p-4 border-t border-slate-100">
              {/* 当前步骤信息 */}
              <div className="space-y-3">
                {step >= 1 && character.name && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">主角</span>
                    <span className="font-medium text-slate-800">{character.name}</span>
                  </div>
                )}
                
                {step >= 1 && character.traits.length > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">性格</span>
                    <div className="flex gap-1">
                      {character.traits.slice(0, 2).map((trait) => (
                        <span key={trait} className="px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-full text-xs">
                          {trait === 'brave' && '勇敢'}
                          {trait === 'kind' && '善良'}
                          {trait === 'curious' && '好奇'}
                          {trait === 'playful' && '调皮'}
                          {trait === 'clever' && '聪明'}
                          {trait === 'friendly' && '友好'}
                        </span>
                      ))}
                      {character.traits.length > 2 && (
                        <span className="text-xs text-slate-400">+{character.traits.length - 2}</span>
                      )}
                    </div>
                  </div>
                )}

                {step >= 2 && story.age && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">目标年龄</span>
                    <span className="font-medium text-slate-800">
                      {story.age === '3-4' && '3-4岁 (AA级)'}
                      {story.age === '5-6' && '5-6岁 (A-B级)'}
                      {story.age === '7-8' && '7-8岁 (C-D级)'}
                    </span>
                  </div>
                )}

                {step >= 2 && story.theme && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">主题</span>
                    <span className="font-medium text-slate-800">{themeLabels[story.theme]}</span>
                  </div>
                )}

                {step >= 3 && visual.style && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">风格</span>
                    <span className="font-medium text-slate-800">{getStyleLabel(visual.style)}</span>
                  </div>
                )}

                {step >= 3 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">AI 模型</span>
                    <span className="font-medium text-slate-800">
                      {'GLM-5V (多模态)'}
                    </span>
                  </div>
                )}
              </div>

              {/* 提示 */}
              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-400">
                <Sparkles className="w-3 h-3" />
                <span>配置将自动保存</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
